import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { formatDate } from '../../utils/dateUtils';
import { useDeleteReading } from '../../hooks/useReadings';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const DeleteReadingModal = ({ isOpen, onClose, reading }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const deleteReading = useDeleteReading();
    const { t } = useLanguage();

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);

        try {
            await deleteReading.mutateAsync({ readingUid: reading.uid });
            onClose();
        } catch (error) {
            console.error('Error deleting reading:', error);
            setDeleteError(error.message || t(KEYS.READING_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.READING_DELETE_TITLE)}
            subtitle={t(KEYS.READING_DELETE_SUBTITLE)}
            size="small"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.READING_DELETE_SUBMITTING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            {deleteError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {deleteError}
                </div>
            )}

            <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-text-primary font-medium">
                        Delete reading <strong>{reading?.value}{reading?.unit ? ` ${reading.unit}` : ''}</strong>?
                    </p>
                </div>

                {reading && (
                    <div className="bg-surface-overlay rounded border border-surface-border p-4 space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">Service</span>
                            <span className="text-sm font-medium text-text-primary">{reading.serviceName || '—'}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">Read Date</span>
                            <span className="text-sm font-mono text-text-primary">{formatDate(reading.read_at)}</span>
                        </div>
                    </div>
                )}

                <p className="text-sm text-text-muted">
                    This reading will be permanently removed from the system.
                </p>
            </div>
        </Modal>
    );
};

export default DeleteReadingModal;
